import { useState } from 'react'
import { ChevronRight, ChevronDown, Search } from 'lucide-react'
import { useBrowseTree, type BrowseNode } from '@/api/naming'

type Props = {
  value: string
  onChange: (nodeId: string, name: string) => void
}

export default function BrowseTreePicker({ value, onChange }: Props) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [query, setQuery] = useState('')

  function toggleExpand(nodeId: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(nodeId)) next.delete(nodeId)
      else next.add(nodeId)
      return next
    })
  }

  const trimmed = query.trim()
  const isSearching = trimmed.length >= 2

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--card)]">
      <div className="flex items-center gap-2 border-b border-[var(--border)] px-3 py-2">
        <Search className="h-4 w-4 text-[var(--muted-foreground)]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Suchen (mind. 2 Zeichen)…"
          className="h-7 w-full bg-transparent text-sm focus:outline-none"
        />
      </div>

      <div className="max-h-80 overflow-y-auto p-1">
        {isSearching ? (
          <SearchResults query={trimmed} value={value} onChange={onChange} />
        ) : (
          <RootList
            value={value}
            onChange={onChange}
            expandedIds={expandedIds}
            toggleExpand={toggleExpand}
          />
        )}
      </div>
    </div>
  )
}

function RootList({
  value,
  onChange,
  expandedIds,
  toggleExpand,
}: {
  value: string
  onChange: (nodeId: string, name: string) => void
  expandedIds: Set<string>
  toggleExpand: (id: string) => void
}) {
  const { data, isLoading } = useBrowseTree()
  if (isLoading) return <div className="px-3 py-2 text-xs text-[var(--muted-foreground)]">Lade…</div>
  const nodes = data?.nodes ?? []
  if (nodes.length === 0)
    return <div className="px-3 py-2 text-xs text-[var(--muted-foreground)]">Keine Kategorien vorhanden.</div>
  return (
    <ul>
      {nodes.map((n) => (
        <TreeNode
          key={n.nodeId}
          node={n}
          depth={0}
          value={value}
          onChange={onChange}
          expandedIds={expandedIds}
          toggleExpand={toggleExpand}
        />
      ))}
    </ul>
  )
}

function TreeNode({
  node,
  depth,
  value,
  onChange,
  expandedIds,
  toggleExpand,
}: {
  node: BrowseNode
  depth: number
  value: string
  onChange: (nodeId: string, name: string) => void
  expandedIds: Set<string>
  toggleExpand: (id: string) => void
}) {
  const isExpanded = expandedIds.has(node.nodeId)
  const isSelected = value === node.nodeId
  const children = useBrowseTree(node.nodeId, undefined)
  const childNodes = children.data?.nodes ?? []
  const hasChildren = childNodes.length > 0

  return (
    <li>
      <div
        className={`flex cursor-pointer items-center gap-1 rounded px-1 py-1 text-sm hover:bg-[var(--accent)] ${
          isSelected ? 'bg-[var(--accent)] font-medium' : ''
        }`}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              toggleExpand(node.nodeId)
            }}
            className="flex h-4 w-4 items-center justify-center text-[var(--muted-foreground)]"
            aria-label={isExpanded ? 'Einklappen' : 'Ausklappen'}
          >
            {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <span className="inline-block h-4 w-4" />
        )}
        <button
          type="button"
          onClick={() => onChange(node.nodeId, node.name)}
          className="flex-1 truncate text-left"
          title={`${node.name} (ID: ${node.nodeId})`}
        >
          {node.name}
        </button>
        <span className="font-mono text-[10px] text-[var(--muted-foreground)]">{node.nodeId}</span>
      </div>
      {isExpanded && hasChildren && (
        <ul>
          {childNodes.map((child) => (
            <TreeNode
              key={child.nodeId}
              node={child}
              depth={depth + 1}
              value={value}
              onChange={onChange}
              expandedIds={expandedIds}
              toggleExpand={toggleExpand}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

function SearchResults({
  query,
  value,
  onChange,
}: {
  query: string
  value: string
  onChange: (nodeId: string, name: string) => void
}) {
  const { data, isLoading } = useBrowseTree(undefined, query)
  if (isLoading) return <div className="px-3 py-2 text-xs text-[var(--muted-foreground)]">Suche…</div>
  const nodes = data?.nodes ?? []
  if (nodes.length === 0)
    return (
      <div className="px-3 py-2 text-xs text-[var(--muted-foreground)]">
        Keine Treffer für „{query}". Operator kann „Eigene ID eingeben" nutzen.
      </div>
    )
  return (
    <ul>
      {nodes.map((n) => {
        const isSelected = value === n.nodeId
        return (
          <li key={n.nodeId}>
            <button
              type="button"
              onClick={() => onChange(n.nodeId, n.name)}
              className={`flex w-full flex-col items-start rounded px-2 py-1.5 text-left text-sm hover:bg-[var(--accent)] ${
                isSelected ? 'bg-[var(--accent)] font-medium' : ''
              }`}
            >
              <span>{n.name}</span>
              <span className="text-[10px] text-[var(--muted-foreground)]">
                {n.path} <span className="ml-1 font-mono">({n.nodeId})</span>
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
